import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PushNotificationService } from '@core/services/push-notification.service';
import { StorageEnum } from '@shared/enums/storage.enum';
import { RegisterEventPayload } from '@shared/interfaces/request/register-event.interface';
import {
  TokenValidation,
  VerifyInvititationsPayload,
} from '@shared/interfaces/token-validation';
import { UserInterface } from '@shared/interfaces/user.interface';

import { SessionKeyEnum } from '@shared/enums/session.key.enum';
import { AttributeDescription } from '@shared/interfaces/attribute-description.interface';
import {
  ChangePasswordPayload,
  LoginPayload,
  RegisterPayload,
  TwoFactorVerificationPayload,
} from '@shared/interfaces/request/authen.interface';
import { ApiResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import {
  EmailActivationStatus,
  RegisterResponse,
} from '@shared/models/authen.model';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { TimeUtils } from '@shared/utils/time-utils';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  from,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  share,
  shareReplay,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';

export const REFRESH_TOKEN_PATH = 'accounts/refresh';
export const LOGOUT_PATH = 'accounts/logout';
export const REVOKE_SESSION_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class AuthenService {
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly openEnforceProfileDialogSource = new Subject<number>();

  private pendingRefresh$: Observable<UserInterface> | null = null;

  private sessionGeneration = 0;

  authenticated$ = new BehaviorSubject<boolean>(false);
  profile$ = new BehaviorSubject<UserInterface>(null);

  restoringSession$ = new BehaviorSubject<boolean>(false);

  baseUrl = 'accounts';
  accessToken: string;

  openEnforceProfileDialog$ =
    this.openEnforceProfileDialogSource.asObservable();

  readonly profileInfo$ = defer(() =>
    this.http.get<UserInterface>(`${this.baseUrl}/info`)
  ).pipe(
    share({
      connector: () => new ReplaySubject<UserInterface>(1),
      resetOnRefCountZero: true,
    })
  );

  constructor(
    private readonly http: HttpClient,
    private readonly cookieService: CookieService
  ) {
    this.cookieService.delete(StorageEnum.accessToken, '/jip-login');

    this.accessToken = this.getAccessToken();
    this.authenticated$.next(!!this.accessToken);
    this.restoringSession$.next(!this.accessToken && !!this.getRefreshToken());
  }

  finishSessionRestore(): void {
    this.restoringSession$.next(false);
  }

  getAccessToken(): string {
    let token = '';
    const expiredTime = this.cookieService.get(StorageEnum.expiredTokenTime);

    if (
      Number(expiredTime) >
      TimeUtils.convertUtcToLocalTimeInSeconds(new Date(), 60)
    ) {
      token = this.cookieService.get(StorageEnum.accessToken);
    }

    return token;
  }

  getUserAttribute(): Observable<AttributeDescription[]> {
    const url = `people/attributes`;
    return this.http.get<AttributeDescription[]>(url);
  }

  getProfileInfo(): Observable<UserInterface> {
    return this.profileInfo$;
  }

  private setCookie(key: string, value: string | boolean): void {
    const expiredDate = TimeUtils.getDateWithOffset(new Date(), 1);

    this.cookieService.set(key, value as string, expiredDate, '/');
  }

  login(dto: LoginPayload) {
    return this.http.post<UserInterface>(`${this.baseUrl}/login`, dto).pipe(
      tap(data => {
        this.handleLoginSuccess(data);
      }),
      catchError((error: ApiResponse) => {
        if (error.status !== 200) {
          return of(error);
        }
        this.authenticated$.next(false);
        this.accessToken = null;

        throw error;
      })
    );
  }

  refreshToken(): Observable<UserInterface> {
    if (!this.pendingRefresh$) {
      const generation = this.sessionGeneration;

      this.pendingRefresh$ = this.http
        .post<UserInterface>(`${this.baseUrl}/refresh`, {
          accessToken: this.getStoredAccessToken(),
          refreshToken: this.getRefreshToken(),
        })
        .pipe(
          filter(() => generation === this.sessionGeneration),
          finalize(() => {
            this.pendingRefresh$ = null;
          }),
          shareReplay(1)
        );
    }

    return this.pendingRefresh$;
  }

  resetSession(): void {
    this.authenticated$.next(false);
    this.restoringSession$.next(false);
    this.accessToken = null;
    this.pendingRefresh$ = null;
    this.sessionGeneration++;

    for (const key of [
      StorageEnum.accessToken,
      StorageEnum.refreshToken,
      StorageEnum.expiredTokenTime,
      StorageEnum.globalFilters,
      StorageEnum.email,
      StorageEnum.mfaToken,
    ]) {
      this.cookieService.delete(key, '/');
    }

    this.clearStrengthenProfileDismissals();
  }

  logout(redirect = ''): void {
    this.revokeSession()
      .pipe(
        finalize(() => {
          this.resetSession();
          this.redirectAfterLogout(redirect);
        })
      )
      .subscribe();
  }

  redirectAfterLogout(redirect: string): void {
    if (redirect) {
      this.goTo('redirect');
    } else if (location.href.includes('my-profile')) {
      this.goTo('/');
    } else {
      this.reloadWithoutParams();
    }
  }

  register(
    dto: RegisterPayload,
    token = null,
    orgId = null
  ): Observable<RegisterResponse | ApiResponse> {
    const payload: Record<string, unknown> = {
      username: dto.username.trim(),
      password: dto.password,
      email: dto.email.trim(),
      firstName: dto.firstName.trim(),
      lastName: dto.lastName.trim(),
    };
    if (token) {
      payload.invitationTokens = [token];
    }

    if (orgId) {
      payload.orgId = orgId;
    }

    return this.http
      .post<RegisterResponse>(`${this.baseUrl}/register`, payload)
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  resendConfirmationEmail(
    email: string
  ): Observable<RegisterResponse | ApiResponse> {
    const payload = {
      email,
    };

    return this.http
      .post<any>(`${this.baseUrl}/register/re-send`, payload)
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  getAccountInfo(
    usernameOrEmail: string
  ): Observable<RegisterResponse | ApiResponse> {
    const decodeUsernameOrEmail = encodeURIComponent(usernameOrEmail);
    return this.http
      .get<RegisterResponse>(
        `${this.baseUrl}/register/user-info?userName=${decodeUsernameOrEmail}`
      )
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  activeAccount(email: string, token: string): Observable<ApiResponse> {
    const payload = {
      email,
      token,
    };

    return this.http
      .post<any>(`${this.baseUrl}/register/activate`, payload)
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  getProfile(): Observable<UserInterface> {
    return this.profile$.asObservable();
  }

  authenticated(): Observable<boolean> {
    return this.authenticated$.asObservable();
  }

  getToken(): string {
    return this.accessToken;
  }

  getStoredAccessToken(): string {
    return this.cookieService.get(StorageEnum.accessToken);
  }

  getRefreshToken(): string {
    return this.cookieService.get(StorageEnum.refreshToken);
  }

  getTokenValidation(token: string): Observable<TokenValidation | ApiResponse> {
    const url = `${this.baseUrl}/token/${token}`;
    return this.http.get<TokenValidation>(url).pipe(
      catchError((error: ApiResponse) => {
        if (error.status === 404) {
          this.goTo('notfound');
          return of(error);
        }
        throw error;
      })
    );
  }

  loginWithLinkedIn(
    code: string,
    redirectUri: string,
    eventId?: number,
    orgId?: number,
    utmSource?: string,
    rememberMe = false
  ): Observable<UserInterface> {
    const url = `${this.baseUrl}/login/linkedin`;
    const payload = {
      code,
      redirect_uri: redirectUri,
      ...(eventId && { eventId }),
      ...(orgId && { orgId }),
      ...(utmSource && { utmSource }),
      rememberMe,
    };

    return this.http.post<UserInterface>(url, payload).pipe(
      catchError((error: ApiResponse) => {
        this.handleLoginFailed();

        throw error;
      })
    );
  }

  registerAccount(
    token: string,
    assignToOrgId?: number,
    eventId?: number,
    utmSource?: string,
    rememberMe = false
  ): Observable<UserInterface> {
    const url = `${this.baseUrl}/register/linkedin`;
    const payload: {
      token: string;
      orgId?: number;
      eventId?: number;
      utmSource?: string;
      rememberMe: boolean;
    } = { token, rememberMe };

    if (assignToOrgId) {
      payload.orgId = assignToOrgId;
    }

    if (utmSource) {
      payload.utmSource = utmSource;
    }

    if (eventId) {
      payload.eventId = eventId;
    }

    return this.http.post<UserInterface>(url, payload).pipe(
      catchError((error: ApiResponse) => {
        this.handleLoginFailed();

        throw error;
      })
    );
  }

  registerEvent(payload: RegisterEventPayload): Observable<UserInterface> {
    const url = `${this.baseUrl}/register/event`;

    return this.http.post<UserInterface>(url, payload);
  }

  verifyInvititations(
    payload: VerifyInvititationsPayload
  ): Observable<TokenValidation> {
    const url = `${this.baseUrl}/token/verify-invitations`;
    return this.http.put<TokenValidation>(url, payload);
  }

  changePassword(
    inputPayload: ChangePasswordPayload,
    newPasswordOnly = false
  ): Observable<any> {
    let payload: ChangePasswordPayload;

    if (newPasswordOnly) {
      payload = {
        newPassword: inputPayload.newPassword.trim(),
      };
    } else {
      payload = {
        newPassword: inputPayload.newPassword.trim(),
        oldPassword: inputPayload.oldPassword.trim(),
      };
    }

    return this.http.post(`${this.baseUrl}/password/change`, payload).pipe(
      catchError((error: ApiResponse) => {
        return this.handleError(error);
      })
    );
  }

  requestResetPassword(usernameOrEmail: string): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/password/reset`, { username: usernameOrEmail })
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  resendRequestResetPassword(email: string, token: string): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/password/reset/re-send`, {
        email,
        token,
      })
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  verifyTwoFactor(payload: TwoFactorVerificationPayload): Observable<any> {
    return this.http
      .post<UserInterface>(`${this.baseUrl}/verify-otp`, payload)
      .pipe(
        tap(data => {
          this.handleLoginSuccess(data);
        }),
        catchError((error: ApiResponse) => {
          this.authenticated$.next(false);
          this.accessToken = null;
          throw error;
        })
      );
  }

  resendOTP(email: string, token: string): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/resend-otp`, {
        email,
        token,
      })
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  commitResetPassword(
    email: string,
    token: string,
    newPassword: string
  ): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/password/reset/process`, {
        email,
        token,
        newPassword,
      })
      .pipe(
        catchError((error: ApiResponse) => {
          return this.handleError(error);
        })
      );
  }

  checkExistedEmail(
    email: string,
    eventId: string,
    utmSource: string
  ): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/email/check-exist`, {
      params: { email, eventId, utmSource },
    });
  }

  checkEmailActivation(email: string): Observable<EmailActivationStatus> {
    return this.http.get<EmailActivationStatus>(
      `${this.baseUrl}/email/check-activation`,
      {
        params: { email },
      }
    );
  }

  goTo(url: string): void {
    const languagePath = this.cookieService.get(StorageEnum.locale) || '';
    FormUtils.navigateTo('/' + languagePath.toLowerCase() + url);
  }

  handleLoginFailed(): void {
    this.resetSession();
  }

  handleLoginSuccess(data: UserInterface): void {
    if (data?.email && data?.token) {
      this.setCookie(StorageEnum.email, data.email);
      this.setCookie(StorageEnum.mfaToken, data.token);
    }

    if (data?.accessToken) {
      this.accessToken = data.accessToken;
      const expiredTime = TimeUtils.convertUtcToLocalTimeInSeconds(
        data.expiredDateTime
      );

      this.setCookie(StorageEnum.expiredTokenTime, expiredTime + '');
      this.setCookie(StorageEnum.isTokenExpired, false);
      this.setCookie(StorageEnum.accessToken, data.accessToken);
      this.setCookie(StorageEnum.refreshToken, data.refreshToken);

      this.cookieService.delete(StorageEnum.isTokenExpired);
      this.cookieService.delete(StorageEnum.isTokenExpired, '/');
      this.authenticated$.next(true);
    } else {
      this.authenticated$.next(true);
      this.accessToken = null;
    }

    this.restoringSession$.next(false);
  }

  handleCheckLastLoggedinUser(profile: UserInterface): void {
    if (!profile) return;
    const redirectInfo = JSON.parse(
      this.cookieService.get(StorageEnum.lastLoggedInUser) || '{}'
    );
    if (redirectInfo) {
      if (redirectInfo.userId && redirectInfo.userId !== profile.userId) {
        localStorage.setItem(StorageEnum.refreshBrowser, JSON.stringify(true));
      }
    }
  }

  updateProfileInfo(enforceProfileEnabled = false): void {
    if (!this.accessToken) {
      return;
    }

    combineLatest([this.getUserAttribute(), this.getProfileInfo()]).subscribe({
      next: ([attributeDescriptions, profile]: [
        AttributeDescription[],
        UserInterface,
      ]) => {
        const enforceDialogDisplayed = this.shouldShowEnforceDialog(
          attributeDescriptions,
          profile
        );

        this.profile$.next(profile);
        this.handleCheckLastLoggedinUser(profile);

        if (enforceProfileEnabled && enforceDialogDisplayed) {
          this.openEnforceProfileDialog(profile.id);
        }
      },
      error: error => {
        if (error.status !== 0) {
          this.resetSession();
          location.reload();
        }
      },
    });
  }

  openEnforceProfileDialog(id: number): void {
    this.openEnforceProfileDialogSource.next(id);
  }

  /* ***********************Private************************ */
  private handleError(error: ApiResponse): Observable<ApiResponse> {
    if (error.status === 400) {
      return of(error);
    }
    throw error;
  }

  private reloadWithoutParams(): void {
    globalThis.location.href = StringUtils.removeAllParamsFromUrl(
      globalThis.location.href
    );
  }

  private shouldShowEnforceDialog(
    attributeDescriptions: AttributeDescription[],
    profile: UserInterface
  ): boolean {
    // Hide popup for tenant 3 only, until the implementation of User Story 21887
    if (+this.cookieService.get(SessionKeyEnum.TENANT_ID) === 3) return false;

    let showProfileEnforcementDialog = false;
    const requiredFields = ['JobTitle', 'LeadCompany'];

    for (const key of requiredFields) {
      const attributeDescription = FormUtils.getFieldOptions(
        attributeDescriptions,
        key
      );

      if (!attributeDescription.required) continue;

      if (key === 'LeadCompany') {
        if (!profile.company) showProfileEnforcementDialog = true;
      } else {
        const profileKey = StringUtils.toLowerCaseFirstLetter(key);

        if (!profile[profileKey]) {
          showProfileEnforcementDialog = true;
        }
      }
    }

    return showProfileEnforcementDialog;
  }

  private clearStrengthenProfileDismissals(): void {
    const keyPrefix = `${StorageEnum.strengthenProfileDismissed}_`;

    try {
      Object.keys(sessionStorage)
        .filter(key => key.startsWith(keyPrefix))
        .forEach(key => sessionStorage.removeItem(key));
    } catch {
      return;
    }
  }

  private revokeSession(): Observable<unknown> {
    const accessToken = this.getStoredAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken && !refreshToken) {
      return of(null);
    }

    return from(this.pushNotificationService.getEndpoint()).pipe(
      switchMap(pushEndpoint =>
        this.http.post(`${this.baseUrl}/logout`, {
          accessToken,
          refreshToken,
          pushEndpoint,
        })
      ),
      timeout(REVOKE_SESSION_TIMEOUT_MS),
      catchError(() => of(null)),
      switchMap(() => this.pushNotificationService.unsubscribe())
    );
  }
}
